import Base from '@studiometa/js-toolkit';
import useResize from '@studiometa/js-toolkit/services/resize';
import debounce from '@studiometa/js-toolkit/utils/debounce';
import Flickity from 'flickity';
import { isDev } from '../config';

/**
 * Duck punch the dragMove function in order to resolve
 * swipping bugs on iOS 13
 *
 * @see https://github.com/metafizzy/flickity/pull/1009
 */
// eslint-disable-next-line func-names
Flickity.prototype.dragMove = function (event, pointer, moveVector) {
  if (!this.isDraggable) {
    return;
  }
  event.preventDefault();

  // reverse if right-to-left
  const direction = this.options.rightToLeft ? -1 : 1;
  if (this.options.wrapAround) {
    // wrap around move. #589
    moveVector.x %= this.slideableWidth;
  }
  let dragX = this.dragStartPosition + moveVector.x * direction;

  if (!this.options.wrapAround && this.slides.length) {
    // slow drag
    const originBound = Math.max(-this.slides[0].target, this.dragStartPosition);
    dragX = dragX > originBound ? (dragX + originBound) * 0.5 : dragX;
    const endBound = Math.min(-this.getLastSlide().target, this.dragStartPosition);
    dragX = dragX < endBound ? (dragX + endBound) * 0.5 : dragX;
  }

  if (this.dragX !== dragX) {
    this.previousDragX = this.dragX;
  }

  this.dragX = dragX;

  this.dragMoveTime = new Date();
  this.dispatchEvent('dragMove', event, [pointer, moveVector]);
};

/**
 * Slider class
 */
export default class Slider extends Base {
  /**
   * Slider config.
   * @return {Object}
   */
  get config() {
    return {
      name: 'Slider',
      log: isDev(),
    };
  }

  /**
   * Breakpoint
   */
  get breakpoint() {
    return useResize().props().breakpoint;
  }

  /**
   * Mounted hook
   */
  mounted() {
    this.isInit = false;
    if (this.$options.breakpoints) {
      this.debouncedMounted = debounce(this.mounted.bind(this), 200);
      this.debouncedDestroyed = debounce(this.destroyed.bind(this), 200);
      if (!this.$options.breakpoints.includes(this.breakpoint)) {
        this.$refs.container.classList.add('flickity-disabled');
        return;
      }
    }

    this.slider = new Flickity(this.$refs.container, {
      cellAlign: 'left',
      contain: true,
      pageDots: false,
      prevNextButtons: false,
      draggable: false,
      wrapAround: this.$options.wrapAround ?? true,
      arrowShape:
        'M50,100c-2.6,0-5.1-1-7.1-2.9l-40-40C-1,53.2-1,46.9,2.9,42.9l40-40C46.9-1,53.2-1,57.1,2.9s3.9,10.2,0,14.1 L24.2,50L57.1,83c3.9,3.9,3.9,10.2,0,14.1C55.1,99.1,52.6,100,50,100z',
      ...this.$options,
      on: {
        change: (index) => {
          if (!this.sliderSecondary || this.sliderSecondary.selectedIndex === index) {
            return;
          }

          [...this.$refs.secondaryItemsContents, ...this.$refs.secondaryItemsInfos].forEach(
            (item) => {
              item.classList.remove('duration-500');
              item.classList.remove('duration-700');

              item.classList.add('duration-300');
              item.classList.add('opacity-0');
            }
          );

          const currentContent = this.$refs.secondaryItemsContents[index];
          const currentInfo = this.$refs.secondaryItemsInfos[index];

          currentContent.classList.remove('duration-300');
          currentInfo.classList.remove('duration-300');

          currentContent.classList.add('translate-y-16');
          currentInfo.classList.add('translate-y-16');

          currentContent.classList.add('duration-500');
          currentInfo.classList.add('duration-700');

          setTimeout(() => {
            this.sliderSecondary.selectCell(index, false, true);

            currentContent.classList.remove('opacity-0');
            currentInfo.classList.remove('opacity-0');

            currentContent.classList.remove('translate-y-16');
            currentInfo.classList.remove('translate-y-16');
          }, 300);
        },
      },
    });

    this.isInit = true;

    if (this.$refs.secondary) {
      this.sliderSecondary = new Flickity(this.$refs.secondary, {
        cellAlign: 'left',
        draggable: false,
        contain: false,
        pageDots: false,
        wrapAround: false,
        prevNextButtons: false,
      });
    }

    if (this.$options.sameHeight === 'true') {
      this.slider.on('resize', debounce(this.setSameCellHeight, 400).bind(this));
      this.slider.dispatchEvent('resize');
    }
  }

  /**
   * Set cells at same height
   */
  setSameCellHeight() {
    // eslint-disable-next-line
    this.slider.getCellElements().forEach((slide) => (slide.children[0].style.height = ''));
    // eslint-disable-next-line
    const heights = this.slider.getCellElements().map((slide) => slide.children[0].offsetHeight);
    // eslint-disable-next-line
    const max = Math.max.apply(Math, heights);
    // eslint-disable-next-line
    this.slider.getCellElements().forEach((slide) => (slide.children[0].style.height = `${max}px`));
  }

  /**
   * next
   */
  onNextClick() {
    this.slider.next();
  }

  /**
   * previous
   */
  onPreviousClick() {
    this.slider.previous();
  }

  /**
   * Destroyed
   *
   * @return {void}
   */
  destroyed() {
    this.slider.destroy();
    if (this.sliderSecondary) {
      this.sliderSecondary.destroy();
    }
    this.isInit = false;
  }

  /**
   * Resized
   *
   * @return {void}
   */
  resized() {
    if (!this.$options.breakpoints || !this.debouncedMounted || !this.debouncedDestroyed) {
      return;
    }
    if (this.isInit && !this.$options.breakpoints.includes(this.breakpoint)) {
      this.debouncedDestroyed();
    }
    if (!this.isInit && this.$options.breakpoints.includes(this.breakpoint)) {
      this.debouncedMounted();
    }
  }
}
